
import React from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
          <header className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-lg text-gray-600">Last updated: December 19, 2024</p>
          </header>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                Hugh Muir Investment Corp ("Company," "we," "us," or "our") collects information you provide directly to us, 
                such as when you create an account, request our services, or communicate with us.
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Name, address, phone number, and email address</li>
                <li>Social Security number and date of birth</li>
                <li>Employment information and income details</li>
                <li>Investment objectives and risk tolerance</li>
                <li>Financial account information and investment history</li>
                <li>Tax identification numbers</li>
              </ul>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Financial Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Bank account and investment account details</li>
                <li>Asset and liability information</li>
                <li>Investment performance data</li>
                <li>Transaction history and portfolio holdings</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use the information we collect to provide, maintain, and improve our investment advisory services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide personalized investment advice and portfolio management</li>
                <li>Execute investment transactions on your behalf</li>
                <li>Prepare financial reports and statements</li>
                <li>Comply with regulatory requirements and legal obligations</li>
                <li>Communicate with you about your accounts and our services</li>
                <li>Conduct risk assessments and suitability analyses</li>
                <li>Improve our services and develop new offerings</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, 
                except in the following circumstances:
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with trusted third-party service providers who assist us in operating our business, 
                including custodians, broker-dealers, and technology providers.
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Legal Requirements</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>To comply with applicable laws and regulations</li>
                <li>To respond to subpoenas, court orders, or legal process</li>
                <li>To protect our rights, property, or safety</li>
                <li>To investigate potential violations of our terms of service</li>
              </ul>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Business Transfers</h3>
              <p className="text-gray-700 mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect your personal information against 
                unauthorized access, alteration, disclosure, or destruction:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Secure data centers with restricted physical access</li>
                <li>Regular security assessments and penetration testing</li>
                <li>Employee training on data protection and confidentiality</li>
                <li>Multi-factor authentication for account access</li>
                <li>Regular software updates and security patches</li>
              </ul>
              <p className="text-gray-700 mb-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to 
                protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal information for as long as necessary to provide our services and comply with legal 
                obligations. Specific retention periods include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Client records: Maintained for at least 5 years after account closure</li>
                <li>Investment advisory records: Retained for 5 years as required by SEC regulations</li>
                <li>Transaction records: Kept for 3 years or as required by applicable law</li>
                <li>Communications: Preserved for regulatory compliance periods</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">
                You have certain rights regarding your personal information:
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Access and Correction</h3>
              <p className="text-gray-700 mb-4">
                You may request access to your personal information and ask us to correct any inaccuracies.
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Communication Preferences</h3>
              <p className="text-gray-700 mb-4">
                You can opt out of receiving promotional communications from us, though we may still send service-related messages.
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Account Closure</h3>
              <p className="text-gray-700 mb-4">
                You may close your account at any time, subject to the terms of your advisory agreement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                Our website uses cookies and similar technologies to enhance your experience:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Essential cookies for website functionality</li>
                <li>Analytics cookies to understand website usage</li>
                <li>Preference cookies to remember your settings</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can control cookie settings through your browser preferences, though disabling certain cookies may affect website functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Links</h2>
              <p className="text-gray-700 mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or 
                content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal 
                information from children under 18. If we become aware that we have collected such information, we will 
                take steps to delete it promptly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. State-Specific Rights</h2>
              <p className="text-gray-700 mb-4">
                Residents of certain states may have additional privacy rights under applicable state laws, including the 
                California Consumer Privacy Act (CCPA) and similar legislation. Please contact us for information about 
                exercising these rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and processed in countries other than your country of residence. 
                We ensure appropriate safeguards are in place to protect your information in accordance with this privacy policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this privacy policy from time to time to reflect changes in our practices or applicable law. 
                We will notify you of any material changes by posting the updated policy on our website and updating the 
                "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Hugh Muir Investment Corp</strong></p>
                <p className="text-gray-700 mb-2">Privacy Officer</p>
                <p className="text-gray-700 mb-2">1 Tiffany Point Suite G 10</p>
                <p className="text-gray-700 mb-2">Bloomington, IL 60618</p>
                <p className="text-gray-700 mb-2">Phone: (773) 850-0803</p>
                <p className="text-gray-700">Email: info@hughmuir.com</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
